//* eslint-disable no-unused-vars */
import React, { useState, useCallback } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, ScrollView,
  TextInput, Alert, ActivityIndicator, Switch, StatusBar,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { normalizeCampaign, API_BASE, getH, getPlatformConfig, postToPlatforms } from '../../../services/campaignAPI';

const P = '#0e4e96';
const Y = '#fdb913';

const ALL_PLATFORMS = [
  { id: 'LinkedIn',        icon: 'linkedin',           color: '#0077B5' },
  { id: 'Twitter',         icon: 'twitter',            color: '#1DA1F2' },
  { id: 'Facebook',        icon: 'facebook',           color: '#1877F2' },
  { id: 'Instagram',       icon: 'instagram',          color: '#E1306C' },
  { id: 'Email',           icon: 'email-outline',      color: '#0e4e96' },
  { id: 'Job Boards',      icon: 'briefcase-outline',  color: '#28a745' },
  { id: 'Industry Forums', icon: 'forum-outline',      color: '#fd7e14' },
  { id: 'Website',         icon: 'web',                color: '#6c757d' },
];

const STATUS_OPTIONS = ['Draft', 'Scheduled', 'Active', 'Paused', 'Completed'];

export default function EditCampaignScreen({ navigation, route }) {
  const raw = route?.params?.campaign || {};
  const c = normalizeCampaign(raw);

  const [saving, setSaving]             = useState(false);
  const [syncing, setSyncing]           = useState(false);

  const [name, setName]                 = useState(c.name);
  const [description, setDescription]  = useState(c.description);
  const [objectives, setObjectives]     = useState(c.objectives?.length ? c.objectives : ['']);
  const [targetAudience, setTargetAudience] = useState(c.targetAudience);
  const [platforms, setPlatforms]       = useState(c.platforms || []);
  const [status, setStatus]             = useState(c.status);
  const [budget, setBudget]             = useState(String(c.budget || ''));
  const [startDate, setStartDate]       = useState(c.startDate || '');
  const [endDate, setEndDate]           = useState(c.endDate || '');
  const [tags, setTags]                 = useState(Array.isArray(c.tags) ? c.tags.join(', ') : (c.tags || ''));
  const [syncAfterSave, setSyncAfterSave] = useState(false);
  const [activeSection, setActiveSection] = useState('basics');

  const SECTIONS = [
    { id: 'basics',    label: 'Basics',    icon: 'information-outline' },
    { id: 'platforms', label: 'Platforms', icon: 'share-variant' },
    { id: 'budget',    label: 'Budget',    icon: 'currency-gbp' },
    { id: 'schedule',  label: 'Schedule',  icon: 'calendar' },
  ];

  const togglePlatform = id => setPlatforms(prev =>
    prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
  );
  const addObjective = () => setObjectives(p => [...p, '']);
  const updateObjective = (text, i) => setObjectives(p => p.map((o, idx) => idx === i ? text : o));
  const removeObjective = i => objectives.length > 1 && setObjectives(p => p.filter((_, idx) => idx !== i));

  const validate = () => {
    if (!name.trim()) { Alert.alert('Required', 'Campaign name is required'); return false; }
    if (!platforms.length) { Alert.alert('Required', 'Select at least one platform'); return false; }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const h = await getH();
      const payload = {
        name:            name.trim(),
        description:     description.trim(),
        type:            status === 'draft' ? 'general' : undefined,
        status,
        budget:          parseFloat(budget) || 0,
        start_date:      startDate,
        end_date:        endDate,
        metrics:         JSON.stringify({
          objectives:      objectives.filter(o => o.trim()),
          target_audience: targetAudience.trim(),
          platforms:       platforms,
          tags:            tags,
        }),
      };
      const res = await fetch(`${API_BASE}/api/data/campaigns/${c.id}`, {
        method: 'PUT', headers: h, body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      const updated = json?.data || { ...raw, ...payload, id: c.id };

      if (syncAfterSave && platforms.length) {
        setSaving(false); setSyncing(true);
        const content = `📢 Updated: ${name}\n\n${description}\n\n${objectives.filter(o => o.trim()).map(o => `• ${o}`).join('\n')}`;
        const results = await postToPlatforms(platforms, content, c.id);
        setSyncing(false);
        const ok = results.filter(r => r.ok).map(r => r.platform);
        const fail = results.filter(r => !r.ok).map(r => r.platform);
        Alert.alert('Saved & Synced',
          `Campaign updated!\n\n✅ Synced to: ${ok.join(', ') || 'none'}${fail.length ? `\n❌ Failed: ${fail.join(', ')}` : ''}`,
          [{ text: 'OK', onPress: () => navigation.navigate('CampaignDetails', { campaign: updated, campaignId: c.id }) }]
        );
      } else {
        Alert.alert('✅ Saved', 'Campaign updated successfully',
          [{ text: 'OK', onPress: () => navigation.navigate('CampaignDetails', { campaign: updated, campaignId: c.id }) }]
        );
      }
    } catch (e) { Alert.alert('Error', e.message); }
    finally { setSaving(false); setSyncing(false); }
  };

  const renderBasics = () => (
    <View>
      <Text style={s.fieldLabel}>Campaign Name *</Text>
      <TextInput style={s.input} value={name} onChangeText={setName} placeholder="Campaign name" placeholderTextColor="#bbb" />

      <Text style={s.fieldLabel}>Status</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 14 }}>
        {STATUS_OPTIONS.map(st => (
          <TouchableOpacity key={st} style={[s.chip, status === st && s.chipActive]} onPress={() => setStatus(st)}>
            <Text style={[s.chipText, status === st && s.chipTextActive]}>{st}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      <Text style={s.fieldLabel}>Description</Text>
      <TextInput style={[s.input, s.inputMulti]} value={description} onChangeText={setDescription} placeholder="Campaign description..." placeholderTextColor="#bbb" multiline />

      <Text style={s.fieldLabel}>Objectives</Text>
      {objectives.map((o, i) => (
        <View key={i} style={s.objRow}>
          <TextInput style={[s.input, { flex: 1, marginBottom: 0 }]} value={o} onChangeText={t => updateObjective(t, i)} placeholder={`Objective ${i + 1}`} placeholderTextColor="#bbb" />
          <TouchableOpacity onPress={() => removeObjective(i)} disabled={objectives.length === 1} style={{ marginLeft: 8 }}>
            <Ionicons name="close-circle" size={22} color={objectives.length === 1 ? '#ddd' : '#ef4444'} />
          </TouchableOpacity>
        </View>
      ))}
      <TouchableOpacity style={s.addObjBtn} onPress={addObjective}>
        <Ionicons name="add-circle-outline" size={18} color={P} />
        <Text style={s.addObjText}>Add Objective</Text>
      </TouchableOpacity>

      <Text style={s.fieldLabel}>Target Audience</Text>
      <TextInput style={[s.input, s.inputMulti]} value={targetAudience} onChangeText={setTargetAudience} placeholder="Target audience..." placeholderTextColor="#bbb" multiline />

      <Text style={s.fieldLabel}>Tags</Text>
      <TextInput style={s.input} value={tags} onChangeText={setTags} placeholder="recruitment, engineering..." placeholderTextColor="#bbb" />
    </View>
  );

  const renderPlatforms = () => (
    <View>
      <Text style={s.sectionNote}>Select where this campaign runs. Toggle "Sync to Social" to push any changes live.</Text>
      <View style={s.platformGrid}>
        {ALL_PLATFORMS.map(p => {
          const sel = platforms.includes(p.id);
          return (
            <TouchableOpacity key={p.id} style={[s.platformCard, sel && { backgroundColor: p.color, borderColor: p.color }]} onPress={() => togglePlatform(p.id)}>
              <MaterialCommunityIcons name={p.icon} size={24} color={sel ? '#fff' : p.color} />
              <Text style={[s.platformCardText, sel && { color: '#fff' }]}>{p.id}</Text>
              {sel && <Ionicons name="checkmark-circle" size={16} color="#fff" style={{ position: 'absolute', top: 6, right: 6 }} />}
            </TouchableOpacity>
          );
        })}
      </View>
      <View style={s.syncRow}>
        <View style={{ flex: 1 }}>
          <Text style={s.syncLabel}>Sync update to social platforms on save</Text>
          <Text style={s.syncSub}>Posts a campaign update to all selected platforms</Text>
        </View>
        <Switch value={syncAfterSave} onValueChange={setSyncAfterSave} trackColor={{ false: '#ddd', true: '#93c5fd' }} thumbColor={syncAfterSave ? P : '#f4f3f4'} />
      </View>
    </View>
  );

  const renderBudget = () => (
    <View>
      <Text style={s.fieldLabel}>Total Budget (£)</Text>
      <TextInput style={s.input} value={budget} onChangeText={setBudget} placeholder="0.00" placeholderTextColor="#bbb" keyboardType="decimal-pad" />
    </View>
  );

  const renderSchedule = () => (
    <View>
      <Text style={s.fieldLabel}>Start Date (YYYY-MM-DD)</Text>
      <TextInput style={s.input} value={startDate} onChangeText={setStartDate} placeholder="2026-04-01" placeholderTextColor="#bbb" />
      <Text style={s.fieldLabel}>End Date (YYYY-MM-DD)</Text>
      <TextInput style={s.input} value={endDate} onChangeText={setEndDate} placeholder="2026-05-01" placeholderTextColor="#bbb" />
    </View>
  );

  return (
    <SafeAreaView style={s.safe}>
      <StatusBar barStyle="light-content" backgroundColor={P} />
      <LinearGradient colors={[P, '#1e7be2']} style={s.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={{ padding: 4 }}>
          <Ionicons name="arrow-back" size={22} color="#fff" />
        </TouchableOpacity>
        <Text style={s.headerTitle}>Edit Campaign</Text>
        <TouchableOpacity onPress={save} disabled={saving || syncing} style={s.saveHeaderBtn}>
          {saving || syncing
            ? <ActivityIndicator size={16} color="#fff" />
            : <Text style={s.saveHeaderBtnText}>{syncing ? 'Syncing...' : 'Save'}</Text>
          }
        </TouchableOpacity>
      </LinearGradient>

      {/* Section nav */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.sectionNav} contentContainerStyle={{ paddingHorizontal: 8, gap: 6, alignItems: 'center' }}>
        {SECTIONS.map(sec => (
          <TouchableOpacity key={sec.id} style={[s.sectionNavBtn, activeSection === sec.id && s.sectionNavBtnActive]} onPress={() => setActiveSection(sec.id)}>
            <MaterialCommunityIcons name={sec.icon} size={14} color={activeSection === sec.id ? P : '#64748b'} />
            <Text style={[s.sectionNavText, activeSection === sec.id && s.sectionNavTextActive]}>{sec.label}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      <ScrollView style={s.content} keyboardShouldPersistTaps="handled">
        {activeSection === 'basics'    && renderBasics()}
        {activeSection === 'platforms' && renderPlatforms()}
        {activeSection === 'budget'    && renderBudget()}
        {activeSection === 'schedule'  && renderSchedule()}
      </ScrollView>

      <View style={s.footer}>
        <TouchableOpacity style={s.cancelBtn} onPress={() => navigation.goBack()}>
          <Text style={s.cancelBtnText}>Cancel</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[s.saveBtn, (saving || syncing) && { opacity: 0.7 }]} onPress={save} disabled={saving || syncing}>
          {saving || syncing
            ? <><ActivityIndicator size={16} color="#fff" /><Text style={s.saveBtnText}>{syncing ? 'Syncing to social...' : 'Saving...'}</Text></>
            : <><MaterialCommunityIcons name="content-save" size={16} color="#fff" /><Text style={s.saveBtnText}>Save Changes</Text></>
          }
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#F0F4F8' },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 12, gap: 10 },
  headerTitle: { flex: 1, fontSize: 17, fontWeight: '800', color: '#fff' },
  saveHeaderBtn: { paddingHorizontal: 12, paddingVertical: 6, backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 8 },
  saveHeaderBtnText: { color: '#fff', fontWeight: '700', fontSize: 14 },
  sectionNav: { backgroundColor: '#fff', maxHeight: 44, borderBottomWidth: 1, borderBottomColor: '#e8ecf0' },
  sectionNavBtn: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8 },
  sectionNavBtnActive: { backgroundColor: '#0e4e9615' },
  sectionNavText: { fontSize: 12, color: '#64748b', fontWeight: '600' },
  sectionNavTextActive: { color: '#0e4e96', fontWeight: '800' },
  content: { flex: 1, padding: 16 },
  fieldLabel: { fontSize: 12, fontWeight: '700', color: '#475569', marginBottom: 6 },
  input: { backgroundColor: '#fff', borderRadius: 10, borderWidth: 1, borderColor: '#e2e8f0', padding: 12, fontSize: 14, color: '#1e293b', marginBottom: 14 },
  inputMulti: { height: 90, textAlignVertical: 'top' },
  chip: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 20, borderWidth: 1.5, borderColor: '#e2e8f0', backgroundColor: '#fff', marginRight: 8 },
  chipActive: { backgroundColor: P, borderColor: P },
  chipText: { fontSize: 12, color: '#64748b', fontWeight: '600' },
  chipTextActive: { color: '#fff' },
  objRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  addObjBtn: { flexDirection: 'row', alignItems: 'center', gap: 5, marginBottom: 14 },
  addObjText: { color: P, fontWeight: '600', fontSize: 13 },
  sectionNote: { fontSize: 13, color: '#64748b', marginBottom: 16, lineHeight: 20 },
  platformGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 16 },
  platformCard: { width: '46%', padding: 12, borderRadius: 14, borderWidth: 1.5, borderColor: '#e2e8f0', backgroundColor: '#fff', alignItems: 'center', gap: 5 },
  platformCardText: { fontSize: 11, fontWeight: '700', color: '#1e293b', textAlign: 'center' },
  syncRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12, padding: 14, gap: 10 },
  syncLabel: { fontSize: 14, fontWeight: '600', color: '#1e293b' },
  syncSub: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
  footer: { flexDirection: 'row', backgroundColor: '#fff', padding: 16, borderTopWidth: 1, borderTopColor: '#e8ecf0', gap: 10 },
  cancelBtn: { paddingHorizontal: 18, paddingVertical: 12, borderRadius: 12, borderWidth: 1, borderColor: '#e2e8f0' },
  cancelBtnText: { fontSize: 14, fontWeight: '600', color: '#64748b' },
  saveBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, backgroundColor: Y, padding: 13, borderRadius: 12 },
  saveBtnText: { color: '#fff', fontWeight: '800', fontSize: 15 },
});
